import type React from "react";
import { useEffect } from "react";

import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Drawer, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Conversation } from "routes/messages/components";
import { BookingFormPanel } from "routes/messages/components/BookingFormPanel";
import { useMessagesSearchParams } from "routes/messages/hooks/useMessagesSearchParams";
import { IconButton } from "shared/atoms/IconButton";
import { useWindowSize } from "utils/hooks";

const SIDE_PANEL_WIDTH = "400px";

interface Props {
  isOpen: boolean;
  patientId: number;
  onClose: () => void;
}

export const ConversationSidePanel: React.FC<Props> = ({ isOpen, patientId, onClose }) => {
  const { t } = useTranslation();
  const { isBookingFormOpen } = useMessagesSearchParams();

  const { width } = useWindowSize();

  useEffect(() => {
    if (width < 1300 && isOpen) {
      onClose();
    }
  }, [width]);

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={isOpen}
      sx={{
        width: 0,
        "& .MuiDrawer-paper": { width: SIDE_PANEL_WIDTH },
      }}
    >
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            flexShrink: 0,
            padding: "16px 16px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3">{t("sidebar.conversation")}</Typography>
          <IconButton onClick={onClose} iconToRight>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", position: "relative" }}>
          <Conversation hideConversationHeader showMessageShortcuts={false} quickRepliesSize="compact" />
          {isBookingFormOpen && (
            <Box sx={{ height: "100%", width: "100%", position: "absolute", top: 0, right: 0, zIndex: 1300 }}>
              <BookingFormPanel patientId={patientId} />
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
